import moment from 'moment';

export const LOG_COMPARISON_ERROR =
  'Ocorreu um erro ao verificar mudanças do usuário';

export const LOG_FIELD_LABELS: Record<string, string> = {
  email: 'e-mail',
  recoveryemail: 'e-mail de recuperação',
  name: 'nome',
  phone: 'telefone',
  gender: 'sexo',
  enablesafeid: 'safeID',
  identificationnumber: 'CPF',
  hasmfa: 'MFA',
  hasmfaenabled: 'MFA habilitado',
  signlink: 'link de assinatura',
  signpath: 'caminho da assinatura',
  photolink: 'link da foto',
  photopath: 'caminho da foto',
  physicianidentificationnumber: 'CRM',
  physicianstate: 'estado',
  internalcode: 'código interno',
  birthdate: 'Data de nascimento',
  poolid: 'pool',
  disabled: 'desativado',
  status: 'status',
  id: 'id',
  password: 'senha',
  countryid: 'país',
  safeidtoken: 'token Safe ID',
  disabledreason: 'motivo da desativação',
  disabledat: 'desativado em',
  isemailverified: 'e-mail verificado',
};

export const LOG_ACTION_LABELS: Record<string, string> = {
  create: 'Criação de usuário',
  update: 'Atualização de usuário',
  delete: 'Exclusão de usuário',
  activate: 'Ativação de usuário',
  securitylockout: 'Bloqueio de segurança',
  updateprofilephoto: 'Atualização da foto de perfil',
  updatesignphoto: 'Atualização da assinatura',
  adminremovemfa: 'Remoção de MFA pelo administrador',
  enablemfa: 'Ativação de MFA',
  disablemfa: 'Desativação de MFA',
  updatepassword: 'Atualização de senha',
};

const PHOTO_KEYS = new Set(['photolink', 'photopath']);
const SIGN_KEYS = new Set(['signlink', 'signpath']);
const SENSITIVE_KEYS = new Set([
  'password',
  'safeidtoken',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'clientsecret',
  'secrettoken',
  'authorization',
  'apikey',
]);

function isSensitiveKey(key: string): boolean {
  const lookup = normalizeLookup(key);
  return (
    SENSITIVE_KEYS.has(lookup) ||
    /(password|secret|token|authorization|apikey|credential)/.test(lookup)
  );
}

export type LogDiff = {
  key: string;
  label: string;
  before: string;
  after: string;
};

export type LogEntry = {
  timestamp: string;
  userId: string;
  action: string;
  ip: string;
  data: unknown;
  comparisonFailed: boolean;
  diffs: LogDiff[];
};

export function normalizeLookup(value: string): string {
  return String(value).toLowerCase().replace(/[\s_-]+/g, '');
}

export function humanizeKey(key: string): string {
  const text = String(key)
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text || String(key);
}

export function translateField(key: string): string {
  const label = LOG_FIELD_LABELS[normalizeLookup(key)];
  return label || humanizeKey(key);
}

export function translateAction(action: string): string {
  if (!action) return '';
  const label = LOG_ACTION_LABELS[normalizeLookup(action)];
  return label || humanizeKey(action);
}

function isDateLike(value: unknown): boolean {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(value.trim());
}

function formatDate(value: unknown): string {
  const parsed = moment(value as string | Date);
  if (!parsed.isValid()) return String(value ?? '');
  const hasTime =
    value instanceof Date ||
    (typeof value === 'string' && /T|\s\d{2}:/.test(value));
  return parsed.format(hasTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');
}

export function formatLogValue(key: string, value: unknown): string {
  const lookup = normalizeLookup(key);

  if (PHOTO_KEYS.has(lookup)) {
    return value == null || value === '' ? '' : 'Foto do usuário';
  }
  if (SIGN_KEYS.has(lookup)) {
    return value == null || value === '' ? '' : 'Assinatura do usuário';
  }
  if (isSensitiveKey(lookup)) {
    return value == null || value === '' ? '' : '••••';
  }
  if (value == null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  if (isDateLike(value)) return formatDate(value);
  if (Array.isArray(value)) {
    return value.map((item) => formatLogValue(key, item)).join(', ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

function isSnapshot(data: unknown): data is Record<string, unknown> {
  return data != null && typeof data === 'object' && !Array.isArray(data);
}

export function parseSnapshot(data: unknown): unknown {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

function hasSnapshotFields(data: unknown): data is Record<string, unknown> {
  return isSnapshot(data) && Object.keys(data).length > 0;
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export function diffSnapshots(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): LogDiff[] {
  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)])
  );
  const diffs: LogDiff[] = [];

  for (const key of keys) {
    if (valuesEqual(before[key], after[key])) continue;
    diffs.push({
      key,
      label: translateField(key),
      before: formatLogValue(key, before[key]),
      after: formatLogValue(key, after[key]),
    });
  }

  return diffs;
}

function formatTimestamp(value: unknown): string {
  const parsed = moment(value as string | Date);
  return parsed.isValid()
    ? parsed.format('DD/MM/YYYY HH:mm')
    : String(value ?? '');
}

function timestampValue(value: unknown): number {
  const parsed = moment(value as string | Date);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

export function buildLogEntries(rawLogs: unknown): LogEntry[] {
  if (!Array.isArray(rawLogs)) return [];

  const items = rawLogs
    .filter((item): item is Record<string, unknown> => {
      return item != null && typeof item === 'object' && !Array.isArray(item);
    })
    .map((item) => ({
      timestamp: item.timestamp,
      userId: String(item.userId ?? ''),
      action: String(item.action ?? ''),
      ip: String(item.ip ?? ''),
      data: parseSnapshot(item.data),
    }));

  const entries = items.map((item, index) => {
    const base = {
      timestamp: formatTimestamp(item.timestamp),
      sortTime: timestampValue(item.timestamp),
      userId: item.userId,
      action: item.action,
      ip: item.ip,
      data: item.data,
    };

    if (!hasSnapshotFields(item.data)) {
      if (item.data == null || typeof item.data !== 'object') {
        return { ...base, comparisonFailed: true, diffs: [] };
      }
      return { ...base, comparisonFailed: false, diffs: [] };
    }

    const older = items
      .slice(index + 1)
      .find((candidate) => hasSnapshotFields(candidate.data));

    if (!older || !hasSnapshotFields(older.data)) {
      return { ...base, comparisonFailed: true, diffs: [] };
    }

    try {
      return {
        ...base,
        comparisonFailed: false,
        diffs: diffSnapshots(older.data, item.data),
      };
    } catch {
      return { ...base, comparisonFailed: true, diffs: [] };
    }
  });

  return entries
    .sort((a, b) => b.sortTime - a.sortTime)
    .map(({ sortTime, ...entry }) => entry);
}
